import React, { useState, useEffect } from 'react';
import { TrendingUp, Award, Clock, DollarSign } from 'lucide-react';
import barrelReplenishmentService from '../../../services/barrelReplenishmentService';

const SupplierPerformancePanel = () => {
  const [suppliers, setSuppliers] = useState([]);
  const [performanceData, setPerformanceData] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadPerformanceData();
  }, []);

  const loadPerformanceData = async () => {
    try {
      setLoading(true);
      const suppliersData = await barrelReplenishmentService?.getSuppliers({ isActive: true });
      setSuppliers(suppliersData);

      const performance = {};
      for (const supplier of suppliersData) {
        const metrics = await barrelReplenishmentService?.getSupplierPerformance(supplier?.id);
        performance[supplier?.id] = metrics;
      }
      setPerformanceData(performance);
    } catch (error) {
      console.error('Error loading performance data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="space-y-3">
            <div className="h-24 bg-gray-200 rounded"></div>
            <div className="h-24 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <Award className="w-5 h-5 text-yellow-600" />
          Performance des Fournisseurs
        </h2>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {suppliers?.map((supplier) => {
          const metrics = performanceData?.[supplier?.id] || {};
          const onTimeRate = metrics?.onTimeDeliveryRate || 0;
          const avgDeliveryTime = metrics?.averageDeliveryTime || 0;

          return (
            <div key={supplier?.id} className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="font-semibold text-gray-900">{supplier?.name}</h3>
                  <p className="text-sm text-gray-600">{supplier?.city}</p>
                </div>
                <div className="flex items-center gap-1">
                  <Award className="w-4 h-4 text-yellow-500" />
                  <span className="text-sm font-medium text-gray-900">
                    {supplier?.quality_rating?.toFixed(1)}
                  </span>
                </div>
              </div>
              <div className="space-y-3">
                {/* Total Orders */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 text-blue-600" />
                    <span className="text-sm text-gray-600">Commandes totales</span>
                  </div>
                  <span className="font-semibold text-gray-900">{metrics?.totalOrders || 0}</span>
                </div>

                {/* On-Time Delivery Rate */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm text-gray-600">Livraison à temps</span>
                    <span className="font-semibold text-gray-900">{onTimeRate?.toFixed(0)}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full ${
                        onTimeRate >= 90
                          ? 'bg-green-600'
                          : onTimeRate >= 70
                          ? 'bg-yellow-600' :'bg-red-600'
                      }`}
                      style={{ width: `${onTimeRate}%` }}
                    />
                  </div>
                </div>

                {/* Average Delivery Time */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-purple-600" />
                    <span className="text-sm text-gray-600">Délai moyen</span>
                  </div>
                  <span className="font-semibold text-gray-900">
                    {avgDeliveryTime > 0 ? `${avgDeliveryTime?.toFixed(0)}j` : 'N/A'}
                  </span>
                </div>

                {/* Total Spent */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <DollarSign className="w-4 h-4 text-green-600" />
                    <span className="text-sm text-gray-600">Total dépensé</span>
                  </div>
                  <span className="font-semibold text-gray-900">
                    {(metrics?.totalSpent || 0)?.toFixed(0)}€
                  </span>
                </div>

                {/* Reliability Score */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm text-gray-600">Fiabilité</span>
                    <span className="font-semibold text-gray-900">
                      {supplier?.reliability_score?.toFixed(1)}/5
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="h-2 rounded-full bg-blue-600"
                      style={{ width: `${(supplier?.reliability_score / 5) * 100}%` }}
                    />
                  </div>
                </div>
              </div>
              {/* Performance Badge */}
              {onTimeRate >= 90 && metrics?.totalOrders >= 5 && (
                <div className="mt-3 px-3 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded-full text-center">
                  🏆 Fournisseur Excellent
                </div>
              )}
            </div>
          );
        })}
      </div>
      {suppliers?.length === 0 && (
        <div className="text-center py-8 text-gray-500">
          <Award className="w-12 h-12 mx-auto mb-2 text-gray-400" />
          <p>Aucune donnée de performance disponible</p>
        </div>
      )}
    </div>
  );
};

export default SupplierPerformancePanel;